/**
 * Code tables tab (Phase 65 / BLU-594 PR2) — the mapping surface.
 *
 * Anatomy hero (hover a card → the owning SKU segment highlights, state-lifted,
 * 200ms per DESIGN §5) over three mapping cards (product types / colours /
 * sizes) and the recognised option-names strip. Shares the page's settings
 * buffer; the sticky save/discard lives in the page action bar while this tab
 * is active.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { productTypeKind } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SkuAnatomy, type AnatomySegment } from './SkuAnatomy';
import { MappingCard } from './MappingCard';
import { OptionNamesStrip } from './OptionNamesStrip';
import { DesignIdSourceSection } from './DesignIdSourceSection';
import type { CodeTable, FocusMappingRequest, ProductSyncCoverage } from '../lib/types';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

interface CodeTablesTabProps {
  /** Folded store-wide scan. Non-null from the FIRST page — fills progressively. */
  coverage: ProductSyncCoverage | null;
  /**
   * BLU-790: the SETTINGS buffer only. Previously this was
   * `coverageQuery.isLoading || buffer.isLoading`, which held the whole tab —
   * mapping cards, sync-tag input, the lot — behind a 32-45s store scan. That
   * is the same head-of-line blocking the streaming fix removes at the network
   * layer, re-created at the render layer. Everything here except three regions
   * comes from the buffer and must paint immediately.
   */
  bufferLoading: boolean;
  /** No coverage page has landed yet — the three scan-fed regions show a pending state. */
  coverageLoading: boolean;
  /** Products examined so far. Drives the honest progress line. */
  scannedProducts: number;
  /** True once Shopify reports no further pages; until then the lists are partial. */
  coverageComplete: boolean;
  /**
   * A scan request is in flight right now. Drives the progress spinner, never
   * `!coverageComplete`: a walk that stopped at its page cap is incomplete but
   * no longer running, and a spinner there would never stop.
   */
  coverageScanning: boolean;
  /** Scan failed — shown inline; never blanks the tab. */
  coverageError: string | null;
  onRescan: () => void;
  isError: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  buffer: ProductSyncSettingsBuffer;
  admin: boolean;
  pendingFocus: FocusMappingRequest | null;
  onFocusConsumed: () => void;
  seenOptionNames: Set<string>;
  /**
   * Phase 86 slice B (86-19 review A): show the Design IDs section (default).
   * Onboarding passes false: the switch is never part of setting up.
   */
  showDesignIdSource?: boolean;
}

function firstCode(map: Record<string, string>, fallback: string): string {
  const values = Object.values(map);
  return values.length > 0 ? values[0]! : fallback;
}

/** First mapped product-type NAME (for the hero's custom-prefix check), or null. */
function firstName(map: Record<string, string>): string | null {
  const keys = Object.keys(map);
  return keys.length > 0 ? keys[0]! : null;
}

export function CodeTablesTab(props: CodeTablesTabProps) {
  const {
    coverage, bufferLoading, coverageLoading, scannedProducts, coverageComplete, coverageScanning, coverageError,
    onRescan, isError, errorMessage, onRetry, buffer, admin, pendingFocus, onFocusConsumed, seenOptionNames,
    showDesignIdSource = true,
  } = props;
  // One source of truth for "there is no coverage to show, and why" — the cards
  // must not re-derive it (an earlier cut keyed only on loading and rendered a
  // COMPLETE meter whenever the scan failed).
  const coverageMissing: 'scanning' | 'unavailable' | null =
    coverage !== null ? null : coverageError !== null ? 'unavailable' : 'scanning';
  const { t } = useTranslation('productSync');
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/5 py-14 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-semibold">{t('states.errorTitle')}</p>
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{errorMessage ?? t('states.errorBody')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>{t('states.retry')}</Button>
      </div>
    );
  }

  // Only the SETTINGS buffer blocks the tab now — it is a single fast DB read.
  // The store scan streams in behind it (see `bufferLoading` in the props).
  if (bufferLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-24 w-full rounded-xl" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-72 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
          <Skeleton className="h-72 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  const typeCode = firstCode(buffer.settings.productTypes, 'TEE');
  const sizeCode = firstCode(buffer.settings.sizes, 'S');
  const colourCode = firstCode(buffer.settings.colours, 'BK');
  const heroTypeName = firstName(buffer.settings.productTypes);
  // The shared type rule: a Mixed type is not custom as a whole, and an org
  // that sells custom only shows CUST even before it maps a type.
  const heroTypeIsCustom = productTypeKind(buffer.settings, heroTypeName ?? '') === 'custom';

  const heroSegments: AnatomySegment[] = [
    // On-demand-custom types wrap the whole SKU with `CUST-`; show it as a
    // leading segment so the hero reflects the real derived shape.
    ...(heroTypeIsCustom ? [{ key: 'custom', text: 'CUST', label: t('anatomy.custom') }] : []),
    { key: 'type', text: typeCode, label: t('anatomy.type') },
    { key: 'size', text: sizeCode, label: t('anatomy.size') },
    { key: 'colour', text: colourCode, label: t('anatomy.colour') },
    { key: 'design', text: 'CAPY1D', label: t('anatomy.design') },
  ];

  const focusFor = (table: CodeTable): string | null =>
    pendingFocus?.table === table && pendingFocus.name ? pendingFocus.name : null;

  const scanSpinner = coverageScanning ? (
    <Loader2
      className="mt-0.5 h-3 w-3 shrink-0 animate-spin motion-reduce:animate-none"
      aria-hidden="true"
      data-testid="code-tables-scan-spinner"
    />
  ) : null;

  return (
    <div className="space-y-4">
      {/* Anatomy hero */}
      <div className="rounded-xl border bg-background p-5">
        <p className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">{t('mapping.heroLabel')}</p>
        <div className="mt-3 flex flex-wrap items-end gap-x-8 gap-y-3">
          <SkuAnatomy segments={heroSegments} highlight={hoveredSegment} size="lg" />
          <p className="max-w-md text-xs text-muted-foreground">{t('mapping.heroExplain')}</p>
        </div>
      </div>

      {/* Design ID source (Phase 86 slice B): the last SKU segment, so it sits
          right under the anatomy. The section decides who sees the control, a
          read-only line, or nothing (platform admins of store SKU orgs only). */}
      {showDesignIdSource && <DesignIdSourceSection buffer={buffer} />}

      {!admin && (
        <p className="text-sm text-muted-foreground/70">{t('mapping.readOnlyNote')}</p>
      )}

      {/*
        Scan status. Never implies a denominator: the walk does not count the
        catalogue up front, so "2,000 of 3,555" would be fabricated. Reports
        only what was actually examined (BLU-777 — caps TRIM visibly).
        `aria-live` so the count settling is announced once, not per page.
      */}
      {coverageError ? (
        <p className="flex flex-wrap items-center gap-2 text-xs text-amber-700" role="status">
          {t('mapping.scanFailed')}
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onRescan}>
            {t('mapping.rescan')}
          </Button>
        </p>
      ) : coverageLoading ? (
        <p className="flex items-start gap-1 text-xs text-muted-foreground" role="status">
          {scanSpinner}
          {t('mapping.scanStarting')}
        </p>
      ) : !coverageComplete ? (
        <p className="flex items-start gap-1 text-xs text-amber-700" role="status" aria-live="polite">
          {scanSpinner}
          {/* `scanned`, never `count`: `count` is i18next's plural selector, and
              handing it a pre-formatted string silently breaks plural resolution. */}
          {t('mapping.scanPartial', { scanned: scannedProducts.toLocaleString() })}
        </p>
      ) : null}

      {/* Three cards */}
      <div className="grid gap-4 lg:grid-cols-3">
        <MappingCard
          table="productTypes"
          segKey="type"
          title={t('mapping.card.productTypes')}
          exampleCode={typeCode}
          coverage={coverage}
          buffer={buffer}
          admin={admin}
          onHoverSegment={setHoveredSegment}
          focusName={focusFor('productTypes')}
          onFocusConsumed={onFocusConsumed}
          coverageMissing={coverageMissing}
        />
        <MappingCard
          table="colours"
          segKey="colour"
          title={t('mapping.card.colours')}
          exampleCode={colourCode}
          coverage={coverage}
          buffer={buffer}
          admin={admin}
          onHoverSegment={setHoveredSegment}
          focusName={focusFor('colours')}
          onFocusConsumed={onFocusConsumed}
          coverageMissing={coverageMissing}
        />
        <MappingCard
          table="sizes"
          segKey="size"
          title={t('mapping.card.sizes')}
          exampleCode={sizeCode}
          coverage={coverage}
          buffer={buffer}
          admin={admin}
          onHoverSegment={setHoveredSegment}
          focusName={focusFor('sizes')}
          onFocusConsumed={onFocusConsumed}
          coverageMissing={coverageMissing}
        />
      </div>

      {/* Sync filter tag — gates which Shopify products enter the candidate
          list (empty = everything active, the pre-existing behavior; set =
          only tagged products, drafts included). Its own section, not a
          MappingCard: it filters the whole catalogue fetch rather than
          mapping one SKU segment. */}
      <div className="rounded-xl border bg-background p-5">
        <label htmlFor="product-sync-tag" className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
          {t('mapping.syncTagLabel')}
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Input
            id="product-sync-tag"
            type="text"
            value={buffer.settings.syncTag}
            onChange={(e) => buffer.setSyncTag(e.target.value)}
            placeholder={t('mapping.syncTagPlaceholder')}
            disabled={!admin}
            maxLength={64}
            className="h-8 w-64 text-xs"
          />
          <p className="max-w-md text-xs text-muted-foreground">
            {buffer.settings.syncTag.trim()
              ? t('mapping.syncTagHintActive', { tag: buffer.settings.syncTag.trim() })
              : t('mapping.syncTagHintEmpty')}
          </p>
        </div>
      </div>

      {/* Option-names strip */}
      <OptionNamesStrip seenOptionNames={seenOptionNames} />
    </div>
  );
}
